import { configureStore, AnyAction, ThunkAction } from '@reduxjs/toolkit';
import Service from './Service';
import type TokenizedApi from '../TokenizedApi';
import { accountSlice } from './Account';
import { contractProseSlice } from './ContractProse';

const DEVTOOLS_ENABLED = !!window.localStorage.getItem(
  'tokenized-sdk-devtools-enable',
);

export function makeReduxStore(tokenizedApi: TokenizedApi | null) {
  return configureStore({
    reducer: {
      [accountSlice.name]: accountSlice.reducer,
      // TODO: Redux toolkit provides TypeScript magic to allow
      // the root state type to be inferred even when the slice name
      // is being specified indirectly as above, but it doesn’t work
      // with a plain object as below, so we can’t do
      // [contractProseSlice.name]: contractProseSlice.reducer
      contractProse: contractProseSlice.reducer,
    },
    // Pass the library context through to thunk action creators
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: tokenizedApi,
        },
        serializableCheck: {
          ignoredActionPaths: ['meta.errorObject'],
        },
      }),
    devTools: DEVTOOLS_ENABLED &&
      !!tokenizedApi && {
        name: tokenizedApi.sessionId
          ? `Tokenized SDK ${tokenizedApi.sessionId}`
          : `Tokenized SDK`,
      },
  });
}

export type TReduxStore = ReturnType<typeof makeReduxStore>;
// Infer the type of the whole Redux state
export type TReduxState = ReturnType<TReduxStore['getState']>;
// Infer the type of the dispatch function
type TDispatch = TReduxStore['dispatch'];
export type TReduxAction = Parameters<TDispatch>[0];
// For type checking async action creators
export type TThunkAction<R = void> = ThunkAction<
  R,
  TReduxState,
  TokenizedApi | null,
  AnyAction
>;
// Annoyingly, this has to rely on knowledge of the exact middleware we’re using
export type ReturnTypeFromActionType<A> = A extends TThunkAction<infer R>
  ? R
  : A;

/** @category SDK Services */
export default class ReduxState extends Service {
  store: TReduxStore;

  /** @internal */
  constructor(tokenizedApi: TokenizedApi) {
    super(tokenizedApi);
    this.store = makeReduxStore(tokenizedApi);
  }

  getState() {
    return this.store.getState();
  }

  // There doesn’t seem to be a way to get TypeScript to automatically do the same
  // generic inference here as it would for the store’s dispatch function that
  // we’re wrapping, and Redux Toolkit doesn’t export a generic to figure out
  // the dispatch return type for an action either, so this does the job with
  // assumptions about the middleware:
  dispatch<A extends TReduxAction>(action: A): ReturnTypeFromActionType<A> {
    return this.store.dispatch(action) as ReturnTypeFromActionType<A>;
  }

  subscribeToSelector<S>(
    selector: (state: TReduxState) => S,
    onChange: (currentState: S) => void,
    notifyInitialValue: boolean,
  ) {
    let currentState = notifyInitialValue
      ? undefined
      : selector(this.getState());
    const handleChange = () => {
      const nextState = selector(this.getState());
      if (nextState !== currentState) {
        currentState = nextState;
        onChange(currentState);
      }
    };

    const unsubscribe = this.store.subscribe(handleChange);
    if (notifyInitialValue) {
      handleChange();
    }
    return unsubscribe;
  }
}
